import { useRef } from 'react';
import { Search } from 'lucide-react';
import { Stock } from './StockComparison';
import StockService from '../../Api_Module/Api_Services/StockService';
import AsyncSelect from 'react-select/async';
import { useTheme } from '../ThemeContext';
interface StockSearchBarProps {
  readonly onSelectStock: (stock: Stock) => void;
  readonly selectedStocks: ReadonlyArray<Stock>;
  readonly onRemoveStock: (symbol: string) => void;
  readonly maxStocks: number;
  readonly onRemovedAllStocks: () => void;
  readonly defaultValue?: Stock;
}
export function StockSearchBar({
  onSelectStock,
  selectedStocks,
  onRemoveStock,
  maxStocks,
  onRemovedAllStocks,
  defaultValue
}: StockSearchBarProps) {
  const searchRef = useRef<HTMLDivElement>(null);
  const { theme } = useTheme();

  const handleSelectStock = (stock: Stock, action: any) => {
    if (action === 'remove-value') {
      onRemoveStock(stock.symbol);
      return;
    }
    onSelectStock(stock);
  };

  const handleClearAll = () => {
    onRemovedAllStocks();
  };

  const handleSearch = async (query: string) => {
    const q = query.trim();
    if (q === '') {
      return [];
    }
    const lc = q.toLowerCase();
    const results = await StockService.getStockSymbolsSearch(q);
    if (!results || results.length === 0) {
      return [];
    }
    // Heuristics: prefer equities, de-prioritize ETFs/funds, and sort by best match
    const isFundLike = (name?: string) => {
      if (!name) return false;
      const n = name.toLowerCase();
      return (
        n.includes('etf') ||
        n.includes('fund') ||
        n.includes('trust') ||
        n.includes('notes') ||
        n.includes('bond')
      );
    };
    const norm = (s?: string) => (s ? String(s).toLowerCase() : '');
    const scored = results
      .map((item: any) => {
        const symbol = String(item.symbol || '').toUpperCase();
        const name = item?.name ?? item?.companyName ?? '';
        const ns = norm(symbol);
        const nn = norm(name);
        let score = 0;
        if (ns.startsWith(lc)) score += 100; // best: symbol starts with query
        else if (ns.includes(lc)) score += 60; // symbol contains query
        if (nn.startsWith(lc)) score += 30; // company starts with query
        else if (nn.includes(lc)) score += 15; // company contains query
        if (isFundLike(name)) score -= 50; // de-prioritize funds/etfs
        return { item, score };
      })
      .sort((a: any, b: any) => b.score - a.score)
      .filter((x: any) => x.score > -50) // drop extremely irrelevant fund-like items
      .slice(0, 25)
      .map(({ item }: any) => {
        const name = item?.name ?? item?.companyName ?? '';
        return {
          value: item.symbol,
          label: `${item.symbol} - ${name}`,
          name
        };
      });
    return scored;
  };

  const CustomDropdownIndicator = (props: any) => (
    <div {...props.innerProps} className="cursor-pointer">
      <Search size={18} className="text-gray-500" />
    </div>
  );

  const isMaxStocksSelected = selectedStocks.length >= maxStocks;

  return (
    <div ref={searchRef} className="relative">
      <div className="flex flex-wrap items-center gap-2 bg-theme-secondary border border-theme-tertiary rounded-lg p-2">
        <div className="relative flex-grow">
          <AsyncSelect
            loadOptions={(inputValue, callback) => {
              if (isMaxStocksSelected) {
                callback([]);
                return;
              }
              handleSearch(inputValue).then((data) => {
                callback(data || []);
              });
            }}
            isClearable={false}
            menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
            menuPosition="fixed"
            cacheOptions
            defaultOptions={false}
            onChange={(_newValue: any, actionMeta: any) => {
              if (actionMeta.action === 'clear') {
                handleClearAll();
              } else if (actionMeta.action === 'remove-value') {
                if (actionMeta.removedValue && actionMeta.removedValue.value !== defaultValue?.symbol) {
                  const stock: Stock = {
                    symbol: actionMeta.removedValue.value,
                    name: actionMeta.removedValue.name ?? (String(actionMeta.removedValue.label || '').split(' - ')[1] || ''),
                    metrics: {}
                  };
                  handleSelectStock(stock, actionMeta.action);
                }
              } else if (actionMeta.option?.value) {
                const stock: Stock = {
                  symbol: actionMeta.option.value,
                  name: actionMeta.option.name ?? (String(actionMeta.option.label || '').split(' - ')[1] || ''),
                  metrics: {}
                };
                handleSelectStock(stock, actionMeta.action);
              }
            }}
            isOptionDisabled={() => isMaxStocksSelected}
            isMulti
            value={selectedStocks.map(stock => ({
              value: stock.symbol,
              label: `${stock.symbol} - ${stock.name}`,
            }))}
            defaultValue={defaultValue ? {
              value: defaultValue.symbol,
              label: `${defaultValue.symbol} - ${defaultValue.name}`,
            } : null}
            styles={{
              control: (base) => ({
                ...base,
                backgroundColor: 'transparent',
                border: 'none',
                boxShadow: 'none',
                minHeight: '36px',
              }),
              input: (base) => ({
                ...base,
                color:  theme === 'dark' ? '#fff' : '#000',
              }),
              placeholder: (base) => ({
                ...base,
                color:  theme === 'dark' ? '#aaa' : '#555',
              }),
              multiValue: (base) => ({
                ...base,
                backgroundColor:  theme === 'dark' ? '#374151' : '#d1d5db',
                color: '#fff',
              }),
              multiValueLabel: (base) => ({
                ...base,
                color:  theme === 'dark' ? '#fff' : '#000',
              }),
              menu: (base) => ({
                ...base,
                backgroundColor:  theme === 'dark' ? '#1f2937' : '#f9fafb',
                color:  theme === 'dark' ? '#fff' : '#000',
                zIndex: 9999,
              }),
              menuPortal: (base) => ({ ...base, zIndex: 9999 }),
              option: (base, state) => ({
                ...base,
                backgroundColor: state.isFocused ?  (theme === 'dark' ? '#374151' : '#d1d5db') : (theme === 'dark' ? '#1f2937' : '#f9fafb'),
                color:  theme === 'dark' ? '#fff' : '#000',
                cursor: 'pointer',
              }),
              indicatorSeparator: (base) => ({
                ...base,
                backgroundColor:  'transparent',
              }),
              dropdownIndicator: (base) => ({
                ...base,
                color:  theme === 'dark' ? '#fff' : '#000',
              }),
            }}
            placeholder={selectedStocks.length === 0 ? 'Search for stocks...' : 'Add more stocks...'}
            components={{ DropdownIndicator: CustomDropdownIndicator, IndicatorSeparator: () => null }}
          />
        </div>
      </div>
    </div>
  );
}